import { type ApolloError, useMutation } from "@apollo/client"
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine"
import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import { attachClosestEdge, extractClosestEdge, type Edge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge"
import { DropIndicator } from "@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box"
import { useSSRDisabled } from "@skyltmax/ds9/hooks"
import cn from "classnames"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import SVGInline from "react-svg-inline"
import invariant from "tiny-invariant"
import ExampleInterstitialCard from "../example_interstitial"
import MinimaticConfigWrapper from "./minimatic_config_wrapper"
import SignPreview from "@/admin/components/sign_preview"
import { getFragment, graphql, type FragmentType } from "@/api"
import { type SignTagExample_ExampleFragment } from "@/api/graphql"
import AddBundleToCart from "@/common/components/add_bundle_to_cart"
import AddExampleToCart from "@/common/components/add_example_to_cart"
import EnableAddToCart from "@/common/components/enable_add_to_cart"
import InlineEdit from "@/common/components/inline_edit"
import { useFeatureIsOn } from "@/common/growthbook-react"
import Shopping from "@/google_shopping/components/shopping"
import { BundleOutline, CopyOutline, EditFill } from "@/skylticons"

const SignTagExampleFragment = graphql(`
  fragment SignTagExample_Example on Example {
    id
    exampleUrl
    rasterImage
    rasterImageUrl
    imageAlt
    title
    addToCartEnabled
    minimatic
    imageFilename
    localTotalVatIncl
    localPrice(vatCode: $vatCode)
    fixPrice
    minimaticPossible
    adminExampleUrl
    sortOrder

    minimaticConfiguration {
      id
      backgroundColors
      backgroundColorsEnabled
      contentColors
      contentColorsEnabled
      fixedColors
      fonts
      fontsEnabled
      fixmethods
      fixmethodsEnabled

      textAlign {
        text1
        text2
        text3
      }
    }

    signTag {
      id
      tag
      locale
    }

    exampleable {
      ... on Bundle {
        id
        showSvg
        previewDoubleSided
        previewSvg
        previewSvgB
        subtitle
        previewClass
        name

        image {
          id
          png
          png2x
          webp
          webp2x
        }
      }

      ... on Sign {
        id
        doubleSided
        fixmethodName
        adminOnly
        previewSvg
        previewSvgB
        subtitle
        previewClass
        minimaticAllowed
        defaultDescription
        signType
        metaProduct
        text1
        text2
        text3
        fabricJson
        dualcolor
        transparent
        multicolor
        contentColor
        fonts
        fixmethod
        backgroundColor
        signTypeNameLocal: signTypeName
        signTypeNameEn

        images {
          id
          png
          png2x
          webp
          webp2x
        }
      }

      ... on ExampleInterstitial {
        id
        locale
      }
    }
  }
`)

const SignCopyDoc = graphql(`
  mutation CopyExampleSign($id: ID!) {
    exampleSignCopy(id: $id) {
      path
      sign {
        id
      }
    }
  }
`)

export const asSign = (exampleable?: SignTagExample_ExampleFragment["exampleable"]) => {
  if (exampleable?.__typename === "Sign") {
    return exampleable
  }

  return null
}

const asBundle = (exampleable?: SignTagExample_ExampleFragment["exampleable"]) => {
  if (exampleable?.__typename === "Bundle") {
    return exampleable
  }

  return null
}

const asExampleInterstitial = (exampleable?: SignTagExample_ExampleFragment["exampleable"]) => {
  if (exampleable?.__typename === "ExampleInterstitial") {
    return exampleable
  }

  return null
}

interface ExampleItemProps {
  signCategoryId: string
  admin: boolean
  adminLoggedIn: boolean
  example: FragmentType<typeof SignTagExampleFragment>
  disconnect: (id: string) => void
  dragInstance: symbol
  business: boolean
}

const ExampleItem: React.FC<ExampleItemProps> = ({
  signCategoryId,
  admin,
  adminLoggedIn,
  example: exampleFragment,
  disconnect,
  dragInstance,
  business,
}) => {
  const { t } = useTranslation()
  const example = getFragment(SignTagExampleFragment, exampleFragment)
  const exampleable = example.exampleable
  const sign = asSign(exampleable)
  const bundle = asBundle(exampleable)
  const exampleInterstitial = asExampleInterstitial(exampleable)

  const hangTypeBlank = !sign?.fixmethodName || sign?.fixmethodName?.trim() === ""
  const previewClass = sign?.previewClass || bundle?.previewClass || ""

  const [copyMutation] = useMutation(SignCopyDoc)
  const exampleUrl = `${example.exampleUrl}?category=${signCategoryId}`

  const adoptSign = (e?: React.MouseEvent<HTMLDivElement>) => {
    const target = e?.target as HTMLElement

    if (
      target.classList.contains("example-to-cart") ||
      target.classList.contains("cart-fill") ||
      target.closest("example-to-cart") ||
      target.classList.contains("example-to-signomatic") ||
      target.closest(".example-to-signomatic") ||
      target.closest(".inline-edit-heading")
    ) {
      return
    }

    e?.preventDefault()

    location.assign(exampleUrl)
  }

  const handleCopySign = () => {
    if (!sign) {
      return
    }

    copyMutation({
      variables: { id: sign.id },
      onCompleted: data => {
        const newSign = data.exampleSignCopy?.sign

        if (newSign) {
          location.assign(data.exampleSignCopy?.path || "")
        }
      },
      onError: (error: ApolloError) => {
        if (error.graphQLErrors[0]) {
          return alert(error.graphQLErrors[0].message)
        }

        console.error(error)
        alert(error)
      },
    })
  }

  const ref = useRef(null)
  const [dragging, setDragging] = useState(false)
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null)

  useEffect(() => {
    const el = ref.current
    invariant(el)

    return combine(
      draggable({
        element: el,
        getInitialData: () => ({ exampleId: example.id, instanceId: dragInstance }),
        onDragStart: () => setDragging(true),
        onDrop: () => setDragging(false),
      }),
      dropTargetForElements({
        element: el,
        getData: ({ input, element }) => {
          const data = { exampleId: example.id }

          return attachClosestEdge(data, {
            input,
            element,
            allowedEdges: ["left", "right"],
          })
        },
        getIsSticky: () => true,
        canDrop: ({ source }) => {
          return source.data.instanceId === dragInstance
        },
        onDragEnter: args => {
          if (args.source.data.exampleId !== example.id) {
            setClosestEdge(extractClosestEdge(args.self.data))
          }
        },
        onDrag: args => {
          if (args.source.data.exampleId !== example.id) {
            setClosestEdge(extractClosestEdge(args.self.data))
          }
        },
        onDragLeave: () => {
          setClosestEdge(null)
        },
        onDrop: () => {
          setClosestEdge(null)
        },
      })
    )
  }, [dragInstance, example])

  useEffect(() => {
    setDragging(false)
  }, [example])

  const ssrDisabled = useSSRDisabled()
  const examplesShoppingV2 = useFeatureIsOn("examples-shopping-9292")

  return (
    <div
      className={cn("example-item sortable relative h-full", {
        "opacity-50": dragging,
      })}
      ref={ref}
      data-id={example.id}
    >
      {closestEdge ? <DropIndicator edge={closestEdge} gap="16px" /> : null}
      {exampleInterstitial ? (
        <div className="example-interstitial flex h-full flex-col">
          <ExampleInterstitialCard id={exampleInterstitial.id} />
        </div>
      ) : (
        <>
          {admin && (
            <div className="example-admin-info relative mx-auto w-full bg-white-3 text-center text-sm">
              <div
                className={cn("ml-1 flex items-center p-1", {
                  "justify-center": examplesShoppingV2,
                })}
              >
                <a className="hover:underline" href={example.adminExampleUrl}>
                  Admin ({examplesShoppingV2 ? example.id : example.exampleable.id})
                </a>
                <button
                  type="button"
                  className="ml-2"
                  onClick={() =>
                    navigator.clipboard.writeText(examplesShoppingV2 ? example.id : example.exampleable.id)
                  }
                  disabled={ssrDisabled}
                >
                  <CopyOutline className="h-6 w-6 text-blue-500 active:text-blue-300" />
                </button>
              </div>
              {!examplesShoppingV2 && (
                <div className="shoppingbox-root">
                  <Shopping
                    exampleId={example.id}
                    exampleableId={example.exampleable.id}
                    price={example.localPrice || ""}
                    priceVat={example.localTotalVatIncl || ""}
                  />
                </div>
              )}
            </div>
          )}
          <div className={`example flex-1 adoptable-${example.id}`} onClick={e => adoptSign(e)}>
            <a
              href={exampleUrl}
              className={cn("signv2 sign example-frame", previewClass, {
                "bg-white-5": example.rasterImage,
                "bg-white-3": !example.rasterImage,
              })}
              data-gtm="lp-example-card"
              draggable={admin ? false : undefined}
            >
              {example.rasterImage ? (
                <picture>
                  <source srcSet={example.rasterImageUrl || ""} type="image/webp" />
                  <img
                    src={`sign/${example.id}/image/${example.imageFilename}`}
                    className="mx-auto"
                    alt={example.imageAlt || ""}
                  />
                </picture>
              ) : sign ? (
                <div className="p-2 sm:p-4">
                  {sign.doubleSided ? (
                    <>
                      <SignPreview svg={sign.previewSvg} svgB={sign.previewSvgB} className="h-[45%]" />
                    </>
                  ) : (
                    <SVGInline component="div" svg={sign.previewSvg || ""} className="h-full" />
                  )}
                </div>
              ) : bundle ? (
                <>
                  {bundle.image?.png && bundle.image.webp ? (
                    <picture className="h-full">
                      <source srcSet={bundle.image.webp} type="image/webp" />
                      <img src={bundle.image.png} className="m-auto p-2 sm:p-4" alt={example.imageAlt || ""} />
                    </picture>
                  ) : bundle.showSvg ? (
                    <div className="p-2 sm:p-4">
                      {bundle.previewDoubleSided ? (
                        <>
                          <SignPreview svg={bundle.previewSvg} svgB={bundle.previewSvgB} className="h-[45%]" />
                        </>
                      ) : (
                        <SVGInline component="div" svg={bundle.previewSvg || ""} className="h-full" />
                      )}
                    </div>
                  ) : (
                    <div className="w-28">
                      <BundleOutline />
                    </div>
                  )}
                </>
              ) : null}
            </a>
            <div className="example-info px-2 py-3 lg:p-4" data-gtm="lp-example-card">
              <h3 className="heading-six lg:heading-five !mb-1">
                {adminLoggedIn ? (
                  <div className="inline-edit-heading">
                    {bundle ? (
                      <InlineEdit id={bundle.id} content={example.title || ""} model="bundle" field="name" />
                    ) : (
                      <InlineEdit id={example.id} content={example.title || ""} model="example" field="heading" />
                    )}
                  </div>
                ) : (
                  <a
                    href={`${example.exampleUrl}?category=${signCategoryId}`}
                    className="text-ink-100"
                    data-gtm="lp-example-card"
                  >
                    {example.title}
                  </a>
                )}
              </h3>
              <div className="paragraph-2xs lg:paragraph-xs lg:!mb-3">{sign?.subtitle || bundle?.subtitle}</div>
              <div className="example-price">
                <span className="heading-six lg:heading-five">{example.localPrice}</span>
                <span className="paragraph-2xs lg:paragraph-xs pl-1">
                  {business ? t("order.exc_vat") : t("order.inc_vat")}
                </span>
              </div>
            </div>
            <div className="example-buttons flex flex-col gap-2 px-2 pb-3 capitalize lg:px-4 lg:pb-4">
              {example.addToCartEnabled &&
                (sign ? (
                  <div className="example-to-cart">
                    <AddExampleToCart
                      signId={sign.id}
                      examplerId={Number(example.id)}
                      signCategoryId={Number(signCategoryId)}
                    />
                  </div>
                ) : (
                  <div className="bundle-to-cart">
                    <AddBundleToCart
                      bundleId={bundle?.id || ""}
                      examplerId={Number(example.id)}
                      signCategoryId={Number(signCategoryId)}
                    />
                  </div>
                ))}
              {sign && (admin || !sign.adminOnly) && !example.fixPrice && (
                <a
                  href={
                    example.minimatic
                      ? `${example.exampleUrl}?category=${signCategoryId}`
                      : `/order/adopt_id?exampler_id=${example.id}&sign=${sign.id}&sign_category_id=${signCategoryId}`
                  }
                  className="example-to-signomatic button button-emphasis button-small mb-0"
                  data-gtm="lp-example-button"
                  draggable="false"
                >
                  <EditFill />
                  {t("sign.redesign_it")}
                </a>
              )}
            </div>
          </div>
          {admin && (
            <div className="example-admin-buttons relative flex h-auto flex-col items-center bg-white-3 p-2">
              <div
                className={cn("flex w-full justify-evenly pb-2", {
                  "border-b-1": sign,
                })}
              >
                {example.minimaticPossible && <MinimaticConfigWrapper example={example} admin={admin} />}
                {sign && (
                  <EnableAddToCart
                    signId={Number(sign.id)}
                    enabled={example.addToCartEnabled}
                    hangTypeBlank={hangTypeBlank}
                  />
                )}
              </div>
              <div className="flex w-full flex-col justify-between pt-2 2xl:flex-row">
                {sign && (
                  <>
                    <a
                      href={`/sign/edit/${sign.id}`}
                      className="button button-secondary sign_edit button-small mb-0 ml-0 h-8 2xl:ml-2"
                      draggable="false"
                      type="button"
                    >
                      Edit design
                    </a>
                    <button
                      className="button button-secondary sign_copy button-small mb-0 ml-0 h-8 2xl:ml-2"
                      draggable="false"
                      onClick={() => handleCopySign()}
                      type="button"
                      disabled={ssrDisabled}
                    >
                      Copy
                    </button>
                  </>
                )}
                {bundle && (
                  <a
                    href={example.adminExampleUrl}
                    className="button button-secondary sign_edit button-small mb-0 ml-0 h-8 2xl:ml-2"
                    draggable="false"
                    type="button"
                  >
                    Edit design
                  </a>
                )}
                <button
                  className="button button-secondary button-error sign_delete button-small mb-0 ml-0 h-8 2xl:ml-2"
                  draggable="false"
                  onClick={() => disconnect(example.id)}
                  type="button"
                  disabled={ssrDisabled}
                >
                  Disconnect
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ExampleItem
